import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { errText, ago } from './api';

function call<TIn extends object, TOut>(name: string) {
  return async (data: TIn): Promise<TOut> => (await httpsCallable<TIn, TOut>(functions, name)(data)).data;
}

export interface IntegrationField { key: string; label: string; isSet: boolean }
export interface Integration {
  source: 'datto' | 'sp' | 'sc' | 'msp' | 'unitrends';
  label: string;
  enabled: boolean;
  baseUrl: string;
  fields: IntegrationField[];
  configured: boolean;
  updatedAt: string | null;
  lastTestOk: boolean | null;
  lastTestAt: string | null;
  lastTestError: string | null;
}

const getIntegrations = call<{ orgId: string }, { integrations: Integration[] }>('getOrgIntegrations');
const setIntegration = call<{
  orgId: string; source: string; enabled: boolean; baseUrl: string; values: Record<string, string>;
}, { ok: boolean; fieldsWritten: string[] }>('setOrgIntegration');
const clearIntegration = call<{ orgId: string; source: string }, { ok: boolean }>('clearOrgIntegration');
const testIntegration = call<{ orgId: string; source: string }, {
  ok: boolean; detail: string; error: string | null;
}>('testOrgIntegration');

/**
 * Per-tenant vendor credentials.
 *
 * Credential VALUES never come back from the server — only a per-field "is it
 * set" flag. So the inputs start blank even when a credential exists, and a
 * blank field on save means "leave it alone". That is why there is no way to
 * view an existing password here: a settings page that can reveal the secret it
 * is editing is one XSS away from leaking every tenant's vendor credentials.
 */
export function IntegrationsPanel({ orgId }: { orgId: string }) {
  const [items, setItems] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try { setItems((await getIntegrations({ orgId })).integrations); }
    catch (e) { setError(errText(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, [orgId]);

  return (
    <section className="panel">
      <h3 className="panel-title">Integrations</h3>
      <p className="panel-note">
        Each organisation collects with its own vendor credentials. Values are held in Secret
        Manager and are never returned to the browser — leave a field blank to keep what is
        already stored.
      </p>
      {error && <div className="banner banner-error">{error}</div>}
      {loading ? <div className="empty">Loading…</div> : (
        <div className="integ-list">
          {items.map((it) => (
            <IntegrationCard key={it.source} orgId={orgId} item={it} onChanged={load} />
          ))}
        </div>
      )}
      <p className="panel-note">
        ShadowProtect and Image Manager share one ShadowControl login — configuring either one is
        enough for both.
      </p>
    </section>
  );
}

function IntegrationCard({ orgId, item, onChanged }: {
  orgId: string; item: Integration; onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [baseUrl, setBaseUrl] = useState(item.baseUrl);
  const [values, setValues] = useState<Record<string, string>>({});
  const [enabled, setEnabled] = useState(item.enabled);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function run(key: string, fn: () => Promise<unknown>) {
    setBusy(key); setErr(null); setMsg(null);
    try { await fn(); onChanged(); }
    catch (e) { setErr(errText(e)); }
    finally { setBusy(null); }
  }

  return (
    <div className="integ">
      <div className="integ-head">
        <div>
          <div className="integ-name">{item.label}</div>
          <div className="mono dim" style={{ fontSize: 11 }}>{item.baseUrl}</div>
        </div>
        <div className="integ-tags">
          {item.configured
            ? <span className="tag tag-ok">configured</span>
            : <span className="tag tag-bad">not configured</span>}
          {!item.enabled && <span className="tag tag-warn">disabled</span>}
          {item.lastTestOk === true && <span className="tag tag-ok">test passed</span>}
          {item.lastTestOk === false && <span className="tag tag-bad">test failed</span>}
          <button className="btn-link" onClick={() => setOpen(!open)}>{open ? 'close' : 'edit'}</button>
        </div>
      </div>

      {item.lastTestAt && (
        <div className="dim" style={{ fontSize: 11, marginTop: 2 }}>
          Last tested {ago(item.lastTestAt)}
          {item.lastTestError ? ` — ${item.lastTestError.slice(0, 120)}` : ''}
        </div>
      )}

      {open && (
        <div className="integ-body">
          {err && <div className="banner banner-error">{err}</div>}
          {msg && <div className="banner">{msg}</div>}

          <div className="row-form">
            <label className="field">
              <span className="field-label">Base URL</span>
              <input className="field-input" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} />
            </label>
            {item.fields.map((f) => (
              <label className="field" key={f.key}>
                <span className="field-label">
                  {f.label} {f.isSet && <span className="dim">· stored</span>}
                </span>
                <input
                  className="field-input" type="password" autoComplete="new-password"
                  placeholder={f.isSet ? '•••••••• (unchanged)' : 'not set'}
                  value={values[f.key] ?? ''}
                  onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                />
              </label>
            ))}
          </div>

          <label className="check-row">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            <span>Enabled — collectors will sync this source for this organisation</span>
          </label>

          <div className="row-form" style={{ marginTop: '.6rem' }}>
            <button
              className="btn btn-primary" disabled={busy !== null}
              onClick={() => run('save', async () => {
                const r = await setIntegration({ orgId, source: item.source, enabled, baseUrl, values });
                setValues({});
                setMsg(r.fieldsWritten.length
                  ? `Saved. Updated: ${r.fieldsWritten.join(', ')}.`
                  : 'Saved. No credential fields changed.');
              })}
            >
              {busy === 'save' ? 'Saving…' : 'Save'}
            </button>

            <button
              className="btn" disabled={busy !== null || !item.configured}
              title={item.configured ? 'Make a real call to the vendor' : 'Configure credentials first'}
              onClick={() => run('test', async () => {
                const r = await testIntegration({ orgId, source: item.source });
                setMsg(r.ok ? `Connection OK — ${r.detail}` : `Failed: ${r.error}`);
              })}
            >
              {busy === 'test' ? 'Testing…' : 'Test connection'}
            </button>

            <button
              className="btn" disabled={busy !== null || !item.configured}
              onClick={() => run('clear', async () => {
                await clearIntegration({ orgId, source: item.source });
                setMsg('Credentials removed.');
              })}
            >
              Clear
            </button>
          </div>

          <p className="panel-note">
            Test makes a real call to the vendor and reports what came back. Worth doing now rather
            than discovering a bad credential at 3am as an empty dashboard.
          </p>
        </div>
      )}
    </div>
  );
}
